import * as UI from "@iyk/music-ui"
import { AppResponse, HttpRequest } from "@iyk/react-router/server"
import { Effect } from "effect"
import React from "react"
import { isRouteErrorResponse, Links, Meta, Outlet, Scripts, ScrollRestoration } from "react-router"
import { AppHandler } from "../server/AppHandler.ts"
import type { Route } from "./+types/root.ts"
import stylesheet from "./app.css?url"
import { Meta as MetaTags } from "./ui/Meta.tsx"

export const links: Route.LinksFunction = () => [
  { rel: "stylesheet", href: stylesheet },
  { rel: "apple-touch-icon", sizes: "180x180", href: UI.Assets.images.AppleTouchIconPngUrl },
  { rel: "icon", type: "image/svg+xml", href: UI.Assets.images.FaviconSvgUrl }
]

export const loader = AppHandler.loader("root")(HttpRequest.get.build()).handle((_, context) => {
  return Effect.gen(function* () {
    const toasts = yield* context.toasts.flash
    return AppResponse.Json({ toasts })
  })
})

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" className="dark">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body className="bg-background dark:bg-[var(--app-background-dark)] text-foreground">
        <UI.MusicProviders>{children}</UI.MusicProviders>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  )
}

export default function App({ loaderData }: Route.ComponentProps) {
  const toast = UI.useToast()

  React.useEffect(() => {
    for (const message of loaderData.toasts) {
      toast.add({
        title: message.title,
        description: message.description,
        type: message.type
      })
    }
  }, [loaderData.toasts])

  return <Outlet />
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  let message = "Unexpected error"
  let details = "Something went wrong on our end. Try refreshing the page, or going back to home."
  let stack: string | undefined

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? "Page not found" : "Error"
    details = error.status === 404 ? "We can't find the page you're looking for" : error.statusText || details
  } else if (import.meta.env.DEV && error && error instanceof Error) {
    details = error.message
    stack = error.stack
  }

  return (
    <React.Fragment>
      <MetaTags title={message} />
      <main className="h-dvh flex flex-col justify-center items-center gap-4 p-4">
        <UI.Icon.IykLogo className="size-7" />
        <div className="flex flex-col items-center justify-center gap-0.5 text-center max-w-[360px] mx-auto">
          <h1 className="text-lg">{message}</h1>
          <p className="text-sm text-gray-11">{details}</p>
        </div>

        {stack && (
          <pre className="w-full p-4 overflow-x-auto text-sm">
            <code>{stack}</code>
          </pre>
        )}
      </main>
    </React.Fragment>
  )
}
